import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2Icon, ListChecksIcon, MessageCircleIcon } from 'lucide-react';
import { NoticeBar } from './NoticeBar';
const cardBase = 'rounded-card bg-white p-4 shadow-lift ring-1 ring-black/5';
export function Hero() {
  const reduceMotion = useReducedMotion();
  const float = (offset: number, delay: number) => reduceMotion ? {} : {
    y: [0, offset, 0],
    transition: {
      duration: 5 + delay,
      repeat: Infinity,
      ease: 'easeInOut',
      delay
    }
  };
  const enter = (delay: number) => ({
    initial: reduceMotion ? {
      opacity: 1
    } : {
      opacity: 0,
      x: 40,
      y: 12
    },
    animate: {
      opacity: 1,
      x: 0,
      y: 0
    },
    transition: {
      duration: 0.5,
      delay,
      ease: 'easeOut' as const
    }
  });
  return <section id="top" className="w-full" data-id="element-51" data-mp="3czfkty10i55i">
      <div className="relative w-full overflow-hidden bg-purple" data-id="element-52" data-mp="381xkux4z309n">
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-purple-soft/40 blur-2xl" data-id="element-53" data-mp="384s6ut3mwt56" />
        <div aria-hidden="true" className="pointer-events-none absolute -left-16 bottom-10 h-56 w-56 rounded-full bg-gold/10 blur-2xl" data-id="element-54" data-mp="3o7vl7nivvtyv" />

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 pb-28 pt-12 sm:px-6 md:grid-cols-2 md:gap-8 md:pb-36 md:pt-20" data-id="element-55" data-mp="39a38f34q1jod">
          <motion.div initial={reduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          ease: 'easeOut'
        }} className="text-center md:text-left" data-id="element-56" data-mp="30hhqfp8wzh0l">
            <p className="font-script text-2xl text-gold sm:text-3xl" data-id="element-57" data-mp="2vi53z5lygbyj">Shopping made easy, swift and reliable.</p>

            <h1 className="mt-3 font-display text-[2.6rem] uppercase leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl" data-id="element-58" data-mp="2hk85g3nxxvh8">
              Let&apos;s Do Your{' '}
              <span className="relative inline-block text-gold" data-id="element-59" data-mp="s2jnhxye22k1">
                Market Runs
                <svg aria-hidden="true" viewBox="0 0 300 18" preserveAspectRatio="none" className="absolute -bottom-1 left-0 h-3 w-full text-gold sm:-bottom-2 sm:h-4" data-id="element-60" data-mp="nbl3anaa4kbi">
                  <motion.path d="M3 12C60 4 120 14 180 8C220 4 260 10 297 6" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" initial={reduceMotion ? {
                  pathLength: 1
                } : {
                  pathLength: 0
                }} animate={{
                  pathLength: 1
                }} transition={{
                  duration: 0.8,
                  delay: 0.45,
                  ease: 'easeInOut'
                }} data-id="element-61" data-mp="ia5kl1vkqpwa" />
                </svg>
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-white/80 sm:text-lg md:mx-0" data-id="element-62" data-mp="3ltgndrclksov">
              Send me your list. I shop at Eko, Balogun and Mile 12. You get everything delivered to your door the next
              day.
            </p>

            <motion.div initial={reduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            delay: 0.25,
            ease: 'easeOut'
          }} className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center md:justify-start" data-id="element-63" data-mp="1x7hv3bmtnyzt">
              <a href="#order" className="rounded-full bg-gold px-8 py-4 text-center text-sm font-bold text-purple-deep shadow-lift transition duration-200 hover:-translate-y-0.5 hover:scale-[1.02] hover:bg-gold-light hover:shadow-xl active:scale-100 sm:text-base" data-id="element-64" data-mp="2g257cu964yzd">
                Start Your Market Run
              </a>
              <a href="#how-it-works" className="rounded-full border-2 border-white/70 px-8 py-4 text-center text-sm font-bold text-white transition duration-200 hover:-translate-y-0.5 hover:scale-[1.02] hover:border-white hover:bg-white/10 active:scale-100 sm:text-base" data-id="element-65" data-mp="umpqa2b9nmz0">
                How It Works
              </a>
            </motion.div>
          </motion.div>

          <div className="relative mx-auto h-[300px] w-full max-w-sm sm:h-[340px] md:h-[380px]" data-id="element-66" data-mp="3vjrre5zkhlpf">
            <motion.div {...enter(0.15)} className="absolute left-0 top-0 w-56 sm:w-64" data-id="element-67" data-mp="e7h9hfmobqpt">
              <motion.div animate={float(-10, 0)} className={cardBase} data-id="element-68" data-mp="9b8mxh65cv8m">
                <div className="flex items-center gap-2" data-id="element-69" data-mp="3hrbttkpfqfvw">
                  <ListChecksIcon className="h-4 w-4 text-purple" aria-hidden="true" data-id="element-70" data-mp="3cr7c5rcsjf43" />
                  <p className="text-xs font-bold uppercase tracking-wide text-purple" data-id="element-71" data-mp="3cpcsshh63v24">Chidinma&apos;s list</p>
                </div>
                <ul className="mt-3 space-y-1.5 text-sm text-ink/75" data-id="element-72" data-mp="3mp3o2ldjye39">
                  <li data-id="element-73" data-mp="2yzsw9te6ju19">Rice 5kg</li>
                  <li data-id="element-74" data-mp="2yyznynzkowh0">Tomatoes, 1 basket</li>
                  <li data-id="element-75" data-mp="2yxofwbz3h2k3">Palm oil, 4 litres</li>
                  <li className="text-ink/45" data-id="element-76" data-mp="2u1hlngg29fhz">+ 6 more items</li>
                </ul>
              </motion.div>
            </motion.div>

            <motion.div {...enter(0.35)} className="absolute right-0 top-28 w-48 sm:top-32 sm:w-52" data-id="element-77" data-mp="19h2ryn25vs71">
              <motion.div animate={float(12, 0.6)} className={`${cardBase} flex items-center gap-3`} data-id="element-78" data-mp="1eaos5fr523ly">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100" data-id="element-79" data-mp="zmaqh85gkl6z">
                  <CheckCircle2Icon className="h-5 w-5 text-green-600" aria-hidden="true" data-id="element-80" data-mp="14hhmdtshkllc" />
                </span>
                <div data-id="element-81" data-mp="1x882qnt8vuah">
                  <p className="text-sm font-bold text-green-700" data-id="element-82" data-mp="1x8rka3ezggnz">Delivered ✓</p>
                  <p className="text-xs text-ink/55" data-id="element-83" data-mp="1j8qmwxxxfrd9">Lekki Phase 1, 9:12am</p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div {...enter(0.55)} className="absolute bottom-0 left-6 w-60 sm:left-10 sm:w-64" data-id="element-84" data-mp="1n8h88m4opfae">
              <motion.div animate={float(-14, 1.2)} className={`${cardBase} bg-[#DCF8C6]`} data-id="element-85" data-mp="7rqm1dxik47m">
                <div className="flex items-center gap-2" data-id="element-86" data-mp="bwggcycixaqf">
                  <MessageCircleIcon className="h-4 w-4 text-[#128C7E]" aria-hidden="true" data-id="element-87" data-mp="2ei3nfz2fpxcf" />
                  <p className="text-xs font-bold text-[#128C7E]" data-id="element-88" data-mp="29ir3bm2y74dj">Swift Errands</p>
                </div>
                <p className="mt-2 text-sm text-ink/80" data-id="element-89" data-mp="24rhbcetxfoyh">Your order don land! 📦</p>
                <p className="mt-1 text-right text-[10px] text-ink/45" data-id="element-90" data-mp="1zso06t244a1h">9:14am</p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        
      </div>

      <NoticeBar data-id="element-91" data-mp="1vozru85npaf6" />
    </section>;
}